import { PlayFormat } from '@omni/kit/services/AnalyticsService/Types';
import { useCallback, useEffect, useRef } from 'react';

import { PlayerVideoRef } from '../components/PlayerVideo/PlayerVideo.types';
import usePlayerVideoCurrentTime from '../components/PlayerVideo/hooks/usePlayerVideoCurrentTime';
import { MEDIA_TYPE } from '../types';
import { sendPlayPositions } from '../utils/analytics';

const debug = require('debug')('tca:KitPlayer:hooks:useSegmentLog');

const SEGMENT_LENGTH = 30; // Media is segmented in 30 second chunks

interface UseSegmentLogProps {
  appKey?: string;
  currentMediaType: MEDIA_TYPE;
  disabled?: boolean;
  isLive?: boolean;
  isSeeking: boolean;
  playerVideoRef?: React.RefObject<PlayerVideoRef | null>;
  playFormat: PlayFormat;
  playId?: string;
}

/**
 * Updates segment log to include the newly given
 * segment. Also sends a new PlayPosition event to the API.
 *
 * @Note If it's a `live_video/audio` it will only send a `segmentLog` of
 * `[0]` since there aren't really any "segments" to calculate because we
 * don't have a reliable duration.
 *
 * It will also only send a new play position event if the current position
 * is `SEGMENT_LENGTH` seconds after the previously stored position that was
 * sent. This is to prevent it from sending many requests very frequently if
 * the current segment and previous segment happen to not match, but are
 * actually really close to each other.
 *
 * @Repo mimics ember-kit `lts-4` kit-play-tracker `updateSegmentLog` function
 * https://subsplash.io/ember/kit/-/blob/lts-4/addon/utils/kit-play-tracker.js?ref_type=heads#L590
 *
 * @Confluence https://subsplash.atlassian.net/wiki/spaces/PLAT/pages/505446585/Media+Analytics+Play+Tracking#Play-Positions
 *
 */
export default ({
  appKey = '',
  currentMediaType,
  disabled = false,
  isLive = true,
  isSeeking,
  playerVideoRef,
  playFormat,
  playId = '',
}: UseSegmentLogProps): void => {
  const segmentLogRef = useRef<number[]>([0]);
  const lastSentPosition = useRef<number>(0);

  const { currentTime } = usePlayerVideoCurrentTime({
    playerVideoRef,
  });

  const sendPlayPosition = useCallback(() => {
    if (!playId || !appKey) return;

    sendPlayPositions({
      appKey,
      playId,
      format: playFormat,
      segmentLength: SEGMENT_LENGTH * 1000, // convert to ms
      segmentLog: segmentLogRef.current,
    });
  }, [appKey, playFormat, playId]);

  const updateSegmentLog = useCallback(
    (currentTimeInSeconds: number) => {
      const segment = Math.floor(currentTimeInSeconds / SEGMENT_LENGTH);
      const lastSegment =
        segmentLogRef.current[segmentLogRef.current.length - 1];

      if (isLive) {
        segmentLogRef.current = [0];

        if (currentTimeInSeconds - lastSentPosition.current >= SEGMENT_LENGTH) {
          sendPlayPosition();
          lastSentPosition.current = currentTimeInSeconds;
        }
      } else if (segment !== lastSegment) {
        segmentLogRef.current.push(segment);
        sendPlayPosition();
      }
    },
    [isLive, sendPlayPosition]
  );

  const resetSegmentLog = useCallback(() => {
    debug('resetSegmentLog');
    segmentLogRef.current = [0];
    lastSentPosition.current = 0;
  }, []);

  useEffect(() => {
    if (disabled) return;

    debug('send initial play position');
    sendPlayPosition();
  }, [disabled, sendPlayPosition]);

  /** Do not update segment during seeking */
  useEffect(() => {
    if (isSeeking) return;

    if (currentTime < 0) return;

    updateSegmentLog(currentTime);
  }, [currentTime, isSeeking, updateSegmentLog]);

  /** Reset segement log if media type is updated */
  useEffect(() => {
    resetSegmentLog();
  }, [currentMediaType, resetSegmentLog]);
};
